<script lang="ts">
  import {
    Plus,
    Trash2,
    KeyRound,
    ShieldAlert,
    ChevronRight,
    ChevronDown,
    Monitor,
    Smartphone,
    Tablet,
    MapPin
  } from 'lucide-svelte';
  import Modal from './ui/Modal.svelte';
  import ConfirmDialog from './ui/ConfirmDialog.svelte';
  import { usersQuery, usersQueryKey } from '$lib/queries/users';
  import { profileQuery } from '$lib/queries/profile';
  import { createUser, updateUser, deleteUser, fetchLoginEvents } from '$lib/api/user';
  import { getErrorMessage } from '$lib/api';
  import { getQueryClient } from '$lib/state/queryClient';
  import { showSuccess, showError } from '$lib/state/toast.svelte';
  import {
    USER_ROLE_LABELS,
    type AdminUser,
    type LoginEvent,
    type UserRole
  } from '$lib/types/user';

  const qc = getQueryClient();
  const usersQ = usersQuery();
  const profileQ = profileQuery();

  let users = $derived([...(usersQ.data ?? [])].sort((a, b) => a.username.localeCompare(b.username)));
  let currentUserId = $derived(profileQ.data?.id ?? null);
  let isAdmin = $derived(profileQ.data?.user_role === 'admin');

  const ROLES: UserRole[] = ['admin', 'user', 'developer', 'tester'];

  let busyId = $state<number | null>(null);

  // Create-user modal
  let showCreate = $state(false);
  let creating = $state(false);
  let form = $state<{ username: string; email: string; password: string; role: UserRole }>({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });

  // Password reset modal
  let resetUser = $state<AdminUser | null>(null);
  let resetPassword = $state('');
  let resetting = $state(false);

  // Delete confirm
  let userToDelete = $state<AdminUser | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: usersQueryKey });
  }

  function formatWhen(iso: string | null): string {
    if (!iso) return 'Never';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  }

  // Login activity (which devices / where from) — fetched lazily on expand.
  let expandedId = $state<number | null>(null);
  let eventsById = $state<Record<number, LoginEvent[]>>({});
  let eventsLoadingId = $state<number | null>(null);
  let eventsErrorById = $state<Record<number, string>>({});

  async function toggleExpand(user: AdminUser) {
    if (expandedId === user.id) {
      expandedId = null;
      return;
    }
    expandedId = user.id;
    if (eventsById[user.id]) return;
    eventsLoadingId = user.id;
    try {
      const events = await fetchLoginEvents(user.id);
      eventsById = { ...eventsById, [user.id]: events };
    } catch (e) {
      eventsErrorById = { ...eventsErrorById, [user.id]: getErrorMessage(e) };
    } finally {
      eventsLoadingId = null;
    }
  }

  function formatLocation(e: LoginEvent): string {
    const parts = [e.city, e.region, e.country].filter(
      (p, i, arr) => p && arr.indexOf(p) === i
    );
    return parts.length ? parts.join(', ') : 'Location unavailable';
  }

  function formatDevice(e: LoginEvent): string {
    const browser = e.browser && e.browser !== 'Unknown' ? e.browser : '';
    const os = e.os && e.os !== 'Unknown' ? e.os : '';
    if (browser && os) return `${browser} on ${os}`;
    return browser || os || e.device || 'Unknown device';
  }

  function deviceIcon(kind: string) {
    if (kind === 'Mobile') return Smartphone;
    if (kind === 'Tablet') return Tablet;
    return Monitor;
  }

  async function submitCreate() {
    if (!form.username.trim() || !form.password.trim()) {
      showError('Username and password are required.');
      return;
    }
    creating = true;
    try {
      await createUser({
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role
      });
      invalidate();
      showSuccess(`Created ${form.username.trim()}.`);
      showCreate = false;
      form = { username: '', email: '', password: '', role: 'user' };
    } catch (e) {
      showError(getErrorMessage(e));
    } finally {
      creating = false;
    }
  }

  async function changeRole(user: AdminUser, role: UserRole) {
    if (role === user.role) return;
    busyId = user.id;
    try {
      await updateUser(user.id, { role });
      invalidate();
      showSuccess(`${user.username} is now ${USER_ROLE_LABELS[role]}.`);
    } catch (e) {
      showError(getErrorMessage(e));
      invalidate();
    } finally {
      busyId = null;
    }
  }

  async function toggleActive(user: AdminUser) {
    busyId = user.id;
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      invalidate();
      showSuccess(`${user.username} ${user.is_active ? 'deactivated' : 'activated'}.`);
    } catch (e) {
      showError(getErrorMessage(e));
    } finally {
      busyId = null;
    }
  }

  async function submitReset() {
    if (!resetUser || !resetPassword.trim()) return;
    resetting = true;
    try {
      await updateUser(resetUser.id, { password: resetPassword });
      showSuccess(`Password reset for ${resetUser.username}.`);
      resetUser = null;
      resetPassword = '';
    } catch (e) {
      showError(getErrorMessage(e));
    } finally {
      resetting = false;
    }
  }

  async function confirmDelete() {
    if (!userToDelete) return;
    const u = userToDelete;
    userToDelete = null;
    try {
      await deleteUser(u.id);
      invalidate();
      showSuccess(`Deleted ${u.username}.`);
    } catch (e) {
      showError(getErrorMessage(e));
    }
  }
</script>

<div class="max-w-5xl mx-auto px-4 py-6">
  <div class="flex items-center justify-between mb-5">
    <div>
      <h1 class="text-lg font-semibold text-text-primary">Users</h1>
      <p class="text-sm text-text-muted mt-0.5">Manage accounts, monitor last activity, and control access.</p>
    </div>
    {#if isAdmin}
      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-accent-primary text-accent-on-primary hover:opacity-90 transition-opacity"
        onclick={() => (showCreate = true)}
      >
        <Plus class="w-4 h-4" />
        New user
      </button>
    {/if}
  </div>

  {#if profileQ.isSuccess && !isAdmin}
    <div class="flex items-center gap-2 rounded border border-border-default bg-bg-hover px-4 py-3 text-sm text-text-secondary">
      <ShieldAlert class="w-4 h-4 text-text-muted" />
      You don't have permission to manage users.
    </div>
  {:else if usersQ.isLoading}
    <div class="text-sm text-text-muted py-8 text-center">Loading users…</div>
  {:else}
    <div class="overflow-x-auto rounded border border-border-default">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-border-default bg-bg-hover text-text-muted text-xs uppercase tracking-wide">
            <th class="text-left font-medium px-3 py-2">User</th>
            <th class="text-left font-medium px-3 py-2">Role</th>
            <th class="text-left font-medium px-3 py-2">Status</th>
            <th class="text-left font-medium px-3 py-2">Last active</th>
            <th class="text-right font-medium px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {#each users as user (user.id)}
            {@const isSelf = user.id === currentUserId}
            {@const isExpanded = expandedId === user.id}
            <tr class="border-b border-border-default last:border-0 hover:bg-bg-hover/50">
              <td class="px-3 py-2">
                <button
                  type="button"
                  class="flex items-start gap-1.5 text-left"
                  onclick={() => toggleExpand(user)}
                  title="View login activity"
                >
                  <span class="mt-0.5 text-text-muted">
                    {#if isExpanded}
                      <ChevronDown class="w-4 h-4" />
                    {:else}
                      <ChevronRight class="w-4 h-4" />
                    {/if}
                  </span>
                  <span>
                    <span class="block font-medium text-text-primary">
                      {user.username}
                      {#if isSelf}<span class="text-text-muted font-normal">(you)</span>{/if}
                    </span>
                    {#if user.email}<span class="block text-xs text-text-muted">{user.email}</span>{/if}
                  </span>
                </button>
              </td>
              <td class="px-3 py-2">
                <select
                  class="bg-bg-base border border-border-default rounded px-2 py-1 text-sm text-text-primary disabled:opacity-50"
                  value={user.role}
                  disabled={!isAdmin || isSelf || busyId === user.id}
                  onchange={(e) => changeRole(user, e.currentTarget.value as UserRole)}
                >
                  {#each ROLES as role}
                    <option value={role}>{USER_ROLE_LABELS[role]}</option>
                  {/each}
                </select>
              </td>
              <td class="px-3 py-2">
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
                  disabled={!isAdmin || isSelf || busyId === user.id}
                  onclick={() => toggleActive(user)}
                  title={isSelf ? 'You cannot change your own status' : 'Toggle active'}
                >
                  <span
                    class="inline-block w-2 h-2 rounded-full {user.is_active ? 'bg-green-500' : 'bg-text-muted'}"
                  ></span>
                  <span class={user.is_active ? 'text-text-secondary' : 'text-text-muted'}>
                    {user.is_active ? 'Active' : 'Disabled'}
                  </span>
                </button>
              </td>
              <td class="px-3 py-2 text-text-secondary">{formatWhen(user.last_active)}</td>
              <td class="px-3 py-2">
                <div class="flex items-center justify-end gap-1">
                  {#if isAdmin}
                    <button
                      type="button"
                      class="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-bg-hover"
                      title="Reset password"
                      onclick={() => {
                        resetUser = user;
                        resetPassword = '';
                      }}
                    >
                      <KeyRound class="w-4 h-4" />
                    </button>
                    {#if !isSelf}
                      <button
                        type="button"
                        class="p-1.5 rounded text-text-muted hover:text-red-600 hover:bg-bg-hover"
                        title="Delete user"
                        onclick={() => (userToDelete = user)}
                      >
                        <Trash2 class="w-4 h-4" />
                      </button>
                    {/if}
                  {/if}
                </div>
              </td>
            </tr>
            {#if isExpanded}
              <tr class="border-b border-border-default last:border-0 bg-bg-hover/30">
                <td colspan="5" class="px-3 py-3">
                  <div class="pl-6">
                    <div class="text-xs font-medium uppercase tracking-wide text-text-muted mb-2">
                      Recent sign-ins (device &amp; estimated location)
                    </div>
                    {#if eventsLoadingId === user.id}
                      <div class="text-sm text-text-muted py-2">Loading activity…</div>
                    {:else if eventsErrorById[user.id]}
                      <div class="text-sm text-red-600 py-2">{eventsErrorById[user.id]}</div>
                    {:else if (eventsById[user.id] ?? []).length === 0}
                      <div class="text-sm text-text-muted py-2">No recorded sign-ins yet.</div>
                    {:else}
                      {@const events = eventsById[user.id] ?? []}
                      <ul class="space-y-2">
                        {#each events as e (e.id)}
                          {@const Icon = deviceIcon(e.device)}
                          <li class="flex items-center gap-3 text-sm">
                            <Icon class="w-4 h-4 shrink-0 text-text-muted" />
                            <span class="text-text-primary font-medium min-w-0">{formatDevice(e)}</span>
                            <span class="inline-flex items-center gap-1 text-text-secondary min-w-0">
                              <MapPin class="w-3.5 h-3.5 shrink-0 text-text-muted" />
                              {formatLocation(e)}
                            </span>
                            {#if e.ip_address}
                              <span class="text-xs text-text-muted font-mono">{e.ip_address}</span>
                            {/if}
                            <span class="ml-auto text-xs text-text-muted whitespace-nowrap">
                              {formatWhen(e.created_at)}
                            </span>
                          </li>
                        {/each}
                      </ul>
                    {/if}
                  </div>
                </td>
              </tr>
            {/if}
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<!-- Create user -->
<Modal bind:open={showCreate} size="md" onclose={() => (showCreate = false)}>
  {#snippet header()}
    <h2 class="text-sm font-semibold text-text-primary">New user</h2>
  {/snippet}
  <div class="space-y-3">
    <label class="block">
      <span class="block text-xs text-text-muted mb-1">Username</span>
      <input
        class="w-full bg-bg-base border border-border-default rounded px-2.5 py-1.5 text-sm text-text-primary"
        bind:value={form.username}
        autocomplete="off"
      />
    </label>
    <label class="block">
      <span class="block text-xs text-text-muted mb-1">Email (optional)</span>
      <input
        class="w-full bg-bg-base border border-border-default rounded px-2.5 py-1.5 text-sm text-text-primary"
        type="email"
        bind:value={form.email}
        autocomplete="off"
      />
    </label>
    <label class="block">
      <span class="block text-xs text-text-muted mb-1">Temporary password</span>
      <input
        class="w-full bg-bg-base border border-border-default rounded px-2.5 py-1.5 text-sm text-text-primary"
        type="text"
        bind:value={form.password}
        autocomplete="off"
      />
    </label>
    <label class="block">
      <span class="block text-xs text-text-muted mb-1">Role</span>
      <select
        class="w-full bg-bg-base border border-border-default rounded px-2.5 py-1.5 text-sm text-text-primary"
        bind:value={form.role}
      >
        {#each ROLES as role}
          <option value={role}>{USER_ROLE_LABELS[role]}</option>
        {/each}
      </select>
    </label>
  </div>
  {#snippet footer()}
    <button
      type="button"
      class="px-3 py-1.5 text-sm rounded border border-border-default text-text-secondary hover:bg-bg-hover"
      onclick={() => (showCreate = false)}
    >
      Cancel
    </button>
    <button
      type="button"
      class="px-3 py-1.5 text-sm rounded bg-accent-primary text-accent-on-primary hover:opacity-90 disabled:opacity-50"
      disabled={creating}
      onclick={submitCreate}
    >
      {creating ? 'Creating…' : 'Create user'}
    </button>
  {/snippet}
</Modal>

<!-- Reset password -->
<Modal open={resetUser !== null} size="sm" onclose={() => (resetUser = null)}>
  {#snippet header()}
    <h2 class="text-sm font-semibold text-text-primary">Reset password</h2>
  {/snippet}
  <div class="space-y-3">
    <p class="text-sm text-text-secondary">
      Set a new password for <span class="font-medium text-text-primary">{resetUser?.username}</span>.
    </p>
    <input
      class="w-full bg-bg-base border border-border-default rounded px-2.5 py-1.5 text-sm text-text-primary"
      type="text"
      placeholder="New password"
      bind:value={resetPassword}
      autocomplete="off"
    />
  </div>
  {#snippet footer()}
    <button
      type="button"
      class="px-3 py-1.5 text-sm rounded border border-border-default text-text-secondary hover:bg-bg-hover"
      onclick={() => (resetUser = null)}
    >
      Cancel
    </button>
    <button
      type="button"
      class="px-3 py-1.5 text-sm rounded bg-accent-primary text-accent-on-primary hover:opacity-90 disabled:opacity-50"
      disabled={resetting || !resetPassword.trim()}
      onclick={submitReset}
    >
      {resetting ? 'Saving…' : 'Save password'}
    </button>
  {/snippet}
</Modal>

<ConfirmDialog
  open={userToDelete !== null}
  title="Delete user"
  message={`Delete ${userToDelete?.username}? This cannot be undone.`}
  confirmText="Delete"
  variant="danger"
  onconfirm={confirmDelete}
  oncancel={() => (userToDelete = null)}
/>
